/* eslint-disable react-hooks/exhaustive-deps */
import { DocumentNode, useApolloClient } from "@apollo/client";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import { dataModuleString, useDataQuery } from "../hooks/useDataModule";
import { createFlexContext } from "../models/flexible/create-flex-context";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexSectionController } from "../models/flexible/flex-section-controller";
import { createInitialRequestConfig } from "../models/flexible/initial-request";
import { createTypeInitialValues } from "../models/flexible/initial-values";
import { TypedFlexEntity } from "../models/flexible/typed-flex-entity";
import { createTypeValidationSchema } from "../models/flexible/validation";
import { Case, FlexEntity, ImpossibleToEditAttribute, Maybe } from "../models/generated/graphql";
import { flexibleFormResponseState, pageLoadingState } from "../store/flexible";
import { userPersonalInfoState } from "../store/user";
import { deserializeFlexEntityData } from "../utils/deserialize-flex-entity-data";
import { distinctBy } from "../utils/distinct-by";
import { combineQueries, getInitialFormDataQuery } from "../utils/flex-entity-utils";
import {
	GET_FLEX_ENTITY_AND_TYPE_BY_INSTANCE_ID,
	GET_FLEX_ENTITY_TYPE_AND_CASES_BY_ID,
	GET_FLEX_ENTITY_TYPE_BY_ID,
	GET_IMPOSSIBLE_TO_EDIT_ATTRIBUTES,
} from "./queries";
import {
	GetFlexEntityAndTypeByInstanceIdQuery,
	GetFlexEntityTypeAndCasesByIdQuery,
	GetFlexEntityTypeByIdQuery,
	GetImpossibleToEditAttributesQuery,
} from "./types/queries.generated";

export type FlexFormPage = {
	subtypeAggregate: FlexContext;
	validationSchema: Yup.AnySchema;
	initialValues: any;
};

export type ReturnType = {
	loading: boolean;
	isFlexEntityMissing: boolean;
	flexContext?: FlexContext;
	pages?: Array<FlexFormPage>;
	data?: any;
	error?: any;
};

const extentWithImpossibleToEditAttributesQuery = (query: DocumentNode, isEditingSubmitted: boolean | undefined) =>
	isEditingSubmitted ? combineQueries([query, GET_IMPOSSIBLE_TO_EDIT_ATTRIBUTES]) : query;

const getQuery = (isEntityId: boolean, isCaseId: boolean, isEditingSubmitted: boolean | undefined) => {
	if (!isEntityId && isCaseId) {
		return extentWithImpossibleToEditAttributesQuery(GET_FLEX_ENTITY_TYPE_AND_CASES_BY_ID, isEditingSubmitted);
	}

	return isEntityId
		? extentWithImpossibleToEditAttributesQuery(GET_FLEX_ENTITY_AND_TYPE_BY_INSTANCE_ID, isEditingSubmitted)
		: GET_FLEX_ENTITY_TYPE_BY_ID;
};

type InitialResponseType =
	| (GetFlexEntityAndTypeByInstanceIdQuery & GetImpossibleToEditAttributesQuery)
	| GetFlexEntityTypeByIdQuery
	| (GetFlexEntityTypeAndCasesByIdQuery & GetImpossibleToEditAttributesQuery);

type UseFlexFormArgs = {
	typeId: Maybe<string>;
	entityId: Maybe<string>;
	dynamicParams: { [key: string]: string };
	isExistingForm: boolean | undefined;
	isEditingSubmitted: boolean;
	isCopy: boolean;
	multiplePatientsCount: number | undefined;
};

export const useFlexForm = ({
	typeId,
	entityId,
	dynamicParams,
	isExistingForm,
	isEditingSubmitted,
	isCopy,
	multiplePatientsCount,
}: UseFlexFormArgs): ReturnType => {
	const setResponse = useSetRecoilState(flexibleFormResponseState);
	const setLoading = useSetRecoilState(pageLoadingState);
	const query = useMemo(() => getQuery(!!entityId, !!dynamicParams.caseId, isEditingSubmitted), [isEditingSubmitted]);

	const {
		loading: typeLoading,
		data: typeAndEntityData,
		error: typeError,
	} = useDataQuery<InitialResponseType>(query, {
		variables: {
			id: entityId || typeId,
			caseIds: dynamicParams.caseId?.split(",") || null,
		},
	});
	const [flexContext, setFlexContext] = useState<Maybe<FlexContext>>(null);
	const [dataQueryResult, setDataQueryResult] = useState<{ loading: boolean; data?: any; error?: any }>({ loading: true });
	const [isFlexEntityMissing, setIsFlexEntityMissing] = useState<boolean>(false);
	const client = useApolloClient();
	const { t } = useTranslation();
	const [{ organization }] = useRecoilState(userPersonalInfoState);

	setLoading(typeLoading);

	useEffect(() => {
		if (!typeAndEntityData) {
			setDataQueryResult({ loading: true });
			setResponse({});

			return;
		}

		if (isExistingForm && !(typeAndEntityData as GetFlexEntityAndTypeByInstanceIdQuery).flexEntities?.length) {
			setIsFlexEntityMissing(true);

			return;
		}

		const flexEntity = (typeAndEntityData as GetFlexEntityAndTypeByInstanceIdQuery).flexEntities?.[0] as unknown as FlexEntity;
		const typedFlexEntity = flexEntity && { ...flexEntity, data: JSON.parse(flexEntity.data) };
		const flexEntityType = flexEntity?.type || (typeAndEntityData as GetFlexEntityTypeByIdQuery).flexEntityTypes[0];
		const cases =
			(typeAndEntityData as GetFlexEntityAndTypeByInstanceIdQuery)?.flexEntities?.flatMap((entity) =>
				entity.cases.length && !isCopy
					? entity.cases
					: distinctBy(
							entity.financialDataPoints
								.filter((dataPoint) => !dataPoint.agreement?.isCaseless)
								.map((dataPoint) => dataPoint.generatedFor),
							(cs) => cs?.id || ""
					  )
			) ||
			(typeAndEntityData as GetFlexEntityTypeAndCasesByIdQuery)?.cases ||
			[];
		const deserializedCases = cases
			.map((caseInstance) => ({
				...caseInstance,
				negotiationRounds: caseInstance?.negotiationRounds.map((round) => ({
					...round,
					data: JSON.parse(round.data),
				})),
				flexEntitiesConnection: {
					edges: caseInstance?.flexEntitiesConnection.edges.map((edge) => ({
						...edge,
						node: {
							...edge.node,
							data: JSON.parse(edge.node.data),
						},
					})),
				},
			}))
			.map((caseInstance) => ({
				...caseInstance,
				flexEntities: caseInstance?.flexEntitiesConnection.edges?.map(({ node }) => node),
			})) as unknown as Array<Case & { flexEntities: Array<TypedFlexEntity<any>> }>;

		const context = createFlexContext({
			tenantId: organization.externalId,
			entityType: flexEntityType,
			flexEntity: typedFlexEntity,
			deserializedCases,
			dynamicParams,
			isEditingSubmitted,
			isCopy,
			impossibleToEditAttributes:
				((typeAndEntityData as GetImpossibleToEditAttributesQuery)
					.GetImpossibleToEditAttributes as Array<ImpossibleToEditAttribute>) || [],
			...(multiplePatientsCount && multiplePatientsCount > 0 ? { noPatients: multiplePatientsCount } : {}),
		});
		setFlexContext(context);
	}, [typeAndEntityData, isEditingSubmitted, multiplePatientsCount]);

	useEffect(() => {
		if (!flexContext) {
			return;
		}

		const requestConfig = createInitialRequestConfig(flexContext);

		if (!flexContext.isEditable) {
			setResponse({ agreementScopes: [] });
			setDataQueryResult({ loading: false });

			return;
		}

		client
			.query({
				query: getInitialFormDataQuery(requestConfig),
				variables: requestConfig.variables,
				fetchPolicy: "no-cache",
				context: { module: dataModuleString },
			})
			.then(({ data }) => {
				const deserializedData = deserializeFlexEntityData(data);
				setResponse(deserializedData);
				setDataQueryResult({ loading: false, data: deserializedData });
			})
			.catch((error) => setDataQueryResult({ loading: false, error }));
	}, [flexContext, client, setResponse]);

	if (typeError || dataQueryResult.error) {
		return { error: typeError || dataQueryResult.error, loading: false, isFlexEntityMissing };
	}

	if (typeLoading || dataQueryResult.loading || !flexContext) {
		return { loading: true, isFlexEntityMissing };
	}

	return {
		loading: false,
		isFlexEntityMissing,
		flexContext,
		pages: flexContext.sectionControllers
			.reduce(
				(a, p) => {
					return a.some((e) => e.page === (p.schema.page ?? 0) || !flexContext.isEditable)
						? a.map((e) =>
								e.page === (p.schema.page ?? 0) || !flexContext.isEditable ? { ...e, sections: [...e.sections, p] } : e
						  )
						: [...a, { page: p.schema.page ?? 0, sections: [p] }];
				},
				[{ page: 0, sections: [] as Array<FlexSectionController> }]
			)
			.sort(sortBySelectors((s) => s.page))
			.map((e) => ({ ...flexContext, sectionControllers: e.sections }))
			.map((subtype) => ({
				subtypeAggregate: subtype,
				validationSchema: createTypeValidationSchema(subtype, dataQueryResult.data, t),
				initialValues: createTypeInitialValues(subtype, flexContext.flexEntity?.data || {}, dataQueryResult.data),
			})),
		data: dataQueryResult.data,
	};
};
